import React, {useEffect, useState} from "react";
import {useGlobalContext} from "../context/globalContext";
import {Toaster, toast} from "react-hot-toast";
import Loading from "../components/Loading";
import TableRenderer from "../components/TableRenderer";
import axios from 'axios';

function Resolved() {
    const {userInfo, queryType, setQueryType} = useGlobalContext();
    const [data, setData] = useState(null);
    const [columns, setColumns] = useState([]);
    const [loading, setLoading] = useState(false);

    const getFilteredColumns = (data) => {
        // Columns to always show
        const alwaysShowColumns = [
            "status",  // Added status to always show
            "date",
            "name",
            "email",
            "number",
            "query_desc",
            "taken_by",
            "comment"
        ];
    
        // Updated column order - status first, removed last_updated_by
        const columnOrder = [
            "status",         // Ensuring status is first
            "query_type",
            "id",
            "date",
            "name",
            "new_name",
            "email",
            "new_email",
            "number",
            "new_number",
            "amount",
            "current_batch",
            "new_batch",
            "current_course",
            "new_course",
            "content_desc",
            "reason",
            "feedback",
            "first_installment",
            "second_installment",
            "deviceName",
            "deviceSpecs",
            "file",
            "query_desc",
            "taken_by",
            "comment"
        ];
    
        // Get unique columns from the data
        const uniqueColumns = Array.from(
            new Set(data.flatMap((response) => Object.keys(response)))
        ).map((key) => {
            const col = {
                id: key,
                header: key,
                accessorKey: key,
                footer: key,
            };
            if (key === 'date') {
                col.sortingFn = (rowA, rowB) => {
                    const dateA = rowA.original.timestamp ? new Date(rowA.original.timestamp) : new Date(0);
                    const dateB = rowB.original.timestamp ? new Date(rowB.original.timestamp) : new Date(0);
                    return dateA - dateB;
                };
            }
            return col;
        });
    
        // Sort columns based on the desired order
        const sortedColumns = uniqueColumns.sort(
            (a, b) => {
                const indexA = columnOrder.indexOf(a.id);
                const indexB = columnOrder.indexOf(b.id);
                // If column isn't in columnOrder, put it at the end
                if (indexA === -1) return 1;
                if (indexB === -1) return -1;
                return indexA - indexB;
            }
        );
    
        // Filter out columns with all empty values and remove unwanted columns
        let filteredColumns = sortedColumns.filter((column) => {
            // List of columns to explicitly remove
            const columnsToRemove = [
                "id", 
                "query_type", 
                "type", 
                "createdAt", 
                "last_updated_by",  // Explicitly remove last_updated_by
                "timestamp",
                "original_id",
                "lastUpdated"      // Also remove lastUpdated if it exists
            ];
    
            // Remove unwanted columns
            if (columnsToRemove.includes(column.id)) {
                return false;
            }
    
            // Keep columns that are in alwaysShowColumns
            if (alwaysShowColumns.includes(column.id)) {
                return true;
            }
    
            // For other columns, check if they have any non-empty values
            return data.some(response => {
                const value = response[column.id];
                return value !== null && value !== "" && value !== undefined;
            });
        });
    
        // Remove taken_by column for non-admin users
        if (!userInfo?.isAdmin) {
            filteredColumns = filteredColumns.filter(
                (column) => column.id !== "taken_by"
            );
        }
    
        return filteredColumns;
    };
    const updateRow = (rowId, updatedFields) => {
        setData(prevData =>
            prevData.map(row =>
                row.id === rowId ? {...row, ...updatedFields} : row
            )
        );
    };
 
    const handleDelete = async (row) => {
        toast.loading("Deleting row...");
    
        try {
            const response = await axios.delete(
                "https://66z6lhu4ge.execute-api.ap-south-1.amazonaws.com/prod/api/queries",
                {
                    data: {
                        queryType: queryType,
                        id: row.id,
                        timestamp: row.timestamp,
                        status: "DONE"
                    }
                }
            );
            console.log(response.data);
            if (!response.status === 200) {
                throw new Error(`${response.type} error! Status: ${response.status}`);
            }
            getData({withToast: false});
            toast.dismiss();
            toast.success("Deleted !");
        } catch (error) {
            //clear toast
            toast.dismiss();
            toast.error(error.message);
            console.error("error:", error);
        }
    };
    const getData = async () => {
        setLoading(true);
        setData(null);
        console.log("Fetching resolved queries...");
        try {
            const response = await axios.post(
                "https://66z6lhu4ge.execute-api.ap-south-1.amazonaws.com/prod/api/getqueries",
                { queryType: queryType, status: "DONE" }
            );
            console.log('API Response:', response.data);
            if (!response.data) {
                throw new Error('No data received from server');
            }
    
            // Process dates and remove unwanted fields
            const processedData = response.data.map((each) => {
                if (each.timestamp) {
                    // Using timestamp for date
                    const dateObject = new Date(each.timestamp);
                    const day = String(dateObject.getDate()).padStart(2, "0");
                    const month = String(dateObject.getMonth() + 1).padStart(2, "0");
                    const year = String(dateObject.getFullYear()).slice(2);
                    each.date = `${day}/${month}/${year}`;
                }
                // Remove original_id field (keep timestamp for sort and delete)
                const { original_id, ...filteredData } = each;
                
                return filteredData;
            });
    
            // Sort by timestamp (descending)
            const sortedData = processedData.sort((a, b) => {
                const dateA = a.timestamp ? new Date(a.timestamp) : new Date(0);
                const dateB = b.timestamp ? new Date(b.timestamp) : new Date(0);
                return dateB - dateA;
            });
    
            // Filter for non-admin users
            const finalData = !userInfo?.isAdmin ? sortedData.filter(row => row.taken_by === userInfo.email) : sortedData;
            
            console.log('Processed data:', finalData);
            setData(finalData);
            setColumns(getFilteredColumns(finalData));
        } catch (error) {
            const errorMessage = error.response?.data?.error || error.message;
            toast.error(errorMessage);
            console.error("Error fetching resolved queries:", error);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        //set the query type to the query in local storage
        const query = localStorage.getItem("queryType");
        //if query is not null then set the query type to the query in local storage
        if (query) {
            setQueryType(query);
        } else {
            setQueryType("nameChange");
            localStorage.setItem("queryType", "nameChange");
        }
        if (userInfo?.email) getData();
    }, [userInfo?.email, queryType]);

    return (
        <div className="p-2 ">
            <div className="flex flex-row gap-3 items-center justify-center  w-fit ">
                <label
                    htmlFor="query-type "
                    className="w-fit text-left pl-1 text-lg font-semibold "
                >
                    Select Query type:
                </label>

                <select
                    className="float-left  border-2 border-theme-yellow-dark inline px-3 py-2 rounded-md  text-black outline-none w-fit  cursor-pointer"
                    value={queryType}
                    onChange={(e) => {
                        setQueryType(e.target.value);
                        localStorage.setItem("queryType", e.target.value);
                    }}
                >
                    <option value="nameChange">Name Change</option>
                    <option value="batchShift">Batch Shift</option>
                    <option value="emi">EMI</option>
                    <option value="refund">Refund</option>
                    <option value="removeCourseAccess">Remove Course Access</option>
                    <option value="feedback">Feedback</option>

                    <option value="numberchange">Number change</option>
                    <option value="emailchange">Email change</option>
                    <option value="contentmissing">Content Missing</option>
                    <option value="coursenotvisible">Course Not Visible</option>
                    <option value="UPIpayment">UPI Payment</option>
                    <option value="grpnotalloted">Group not alloted</option>
                    <option value="technicalIssue">Technical Issue</option>

                    <option value="misc">Misc</option>
                </select>
            </div>
            <Toaster
                position="bottom-left"
                toastOptions={{
                    // Define default options
                    className: "",

                    style: {
                        background: "#ff8e00",
                        color: "#2e2c2d",
                    },

                    // Default options for specific types
                    success: {
                        duration: 2000,
                        theme: {
                            primary: "green",
                            secondary: "black",
                        },
                    },
                }}
            />

            {loading && !data ? (
                <Loading/>
            ) : data?.length == 0 ? (
                <p className="text-xl  mt-5 text-center w-full">
                    No Data of the selected type found.
                </p>
            ) : (
                data &&
                columns && (
                    <TableRenderer
                        updateRow={updateRow}
                        data={data} columns={columns} getData={getData} handleDelete={handleDelete} showEdit={false} defaultSortDesc={true} />
                )
            )}
        </div>
    );
}

export default Resolved;